import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useOutletContext } from 'react-router-dom';
import { useProducts, getProductPriceInfo, ProductPriceLabel } from '../../../hooks/useProducts.jsx';
import { useRefreshHubSessionOnMount } from '../../../hooks/useRefreshHubSessionOnMount.js';
import LoadingSpinner from '../../common/LoadingSpinner.jsx';
import { buildApiUrl, freshFetchInit } from '../../../utils/apiBase.js';
import {
  toLicenseName,
  normalizeTimestamp,
  formatYen,
  getProductLicenseApiName,
  isProductRecord,
  isProductLicensed,
  findContractForProduct,
  isTrialFreeBenefit,
  isDeferralFreeBenefit,
} from '../../../utils/licenseUtils.js';
import { resolveEffectiveTrialPeriodDays, describeTrialPeriodSource } from '../../../utils/productTrial.js';
import {
  lastUsageDayDateFromCutoff,
  getNextBillingAnchorDate,
  cutoffMsForJstMonthEnd,
  cutoffMsFromStartAndDays,
  lastUsageDayMsFromCutoff,
  diffCalendarDaysJst,
  isExpiryUsable,
} from '../../../utils/jstDateHelper.js';
import { useBeta } from '../../../contexts/BetaContext.jsx';
import { useClinicPaymentMethodStatus } from '../../../hooks/useClinicPaymentMethodStatus.js';
import { useToast } from '../../../contexts/ToastContext.jsx';
import { notifyBillingDataChanged, BILLING_DATA_CHANGED_EVENT } from '../../../utils/hubBillingEvents.js';
import { estimateProratedAmount } from '../../../utils/prorationEstimate.js';

// 解約理由の選択肢
const CANCEL_REASONS = [
  { id: 'price', label: '料金が高い' },
  { id: 'not_needed', label: '機能を使わなくなった' },
  { id: 'difficult', label: '使い方が難しい' },
  { id: 'alternative', label: '他のサービスに乗り換える' },
  { id: 'temporary', label: '一時的に利用を停止したい' },
  { id: 'business', label: '事業・業務の変更' },
  { id: 'other', label: 'その他' },
];

function isPastDueGraceActiveClient(contract, nowMs = Date.now()) {
  if ((contract?.status || '') !== 'past_due') return false;
  const graceUntil = normalizeTimestamp(contract.pastDueGraceUntil);
  if (graceUntil && graceUntil.getTime() <= nowMs) return false;
  return true;
}

/**
 * ライセンス管理ページ
 */
const LicensesPage = () => {
  const { user, licenses, setLicenses } = useOutletContext();
  const location = useLocation();
  const canManageLicenses = user?.role === 'doctor' || user?.role === 'admin';
  const { effectiveIsBeta, betaFeaturesRestricted, isHubSettingsReady, betaApiHeaders, trialPeriodDays, betaTrialPeriodDays } = useBeta();
  const { hasPaymentMethod, loading: paymentMethodLoading } = useClinicPaymentMethodStatus({ enabled: canManageLicenses });
  const { showToast } = useToast();
  const { products, loading: productsLoading, error: productsError } = useProducts({ freshOnMount: true });
  useRefreshHubSessionOnMount();
  const [localLicenses, setLocalLicenses] = useState(licenses);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelTargetProduct, setCancelTargetProduct] = useState(null);
  const [cancelModalStep, setCancelModalStep] = useState(1); // 1: 確認画面, 2: 理由入力画面
  const [submitting, setSubmitting] = useState(false);
  const [actionError, setActionError] = useState('');
  // 解約理由フォーム用
  const [cancelReasons, setCancelReasons] = useState([]);
  const [cancelFreeComment, setCancelFreeComment] = useState('');
  const [contracts, setContracts] = useState([]);
  const [contractsLoading, setContractsLoading] = useState(true);
  const [contractsError, setContractsError] = useState('');
  const [billingRollup, setBillingRollup] = useState(null);
  const [billingRollupLoading, setBillingRollupLoading] = useState(false);
  const [billingRollupError, setBillingRollupError] = useState('');
  const [paymentInfoRequired, setPaymentInfoRequired] = useState(false);
  /** 有料製品の契約モーダル用。Stripe に問い合わせた結果。null=未取得/取得中 */
  const [paymentMethodStatus, setPaymentMethodStatus] = useState(null);
  const hasLoadedBillingRollup = useRef(false);

  const showBetaSummary = isHubSettingsReady && effectiveIsBeta;

  useEffect(() => {
    setLocalLicenses(licenses);
  }, [licenses]);

  useEffect(() => {
    // 契約状態の読取は全ロール可（サーバ側 GET /license-contracts は requireUser）。
    // 解約・請求ロールアップ等の操作は canManageLicenses でゲート。
    const fetchContracts = async () => {
      setContractsLoading(true);
      setContractsError('');
      try {
        const response = await fetch(buildApiUrl('/api/clinic/license-contracts'), freshFetchInit({
          method: 'GET',
        }));
        const data = await parseJsonResponse(response);
        if (!response.ok) {
          throw new Error(data.message || 'ライセンス契約の取得に失敗しました');
        }
        setContracts(data.contracts || []);
      } catch (err) {
        setContractsError(err.message || 'ライセンス契約の取得に失敗しました');
      } finally {
        setContractsLoading(false);
      }
    };

    fetchContracts();
  }, []);

  const fetchBillingRollup = useCallback(async ({ silent = false } = {}) => {
    if (!canManageLicenses) {
      setBillingRollup(null);
      setBillingRollupLoading(false);
      setBillingRollupError('');
      return;
    }
    if (!silent) {
      setBillingRollupLoading(true);
      setBillingRollupError('');
    }
    try {
      const response = await fetch(buildApiUrl('/api/clinic/billing-rollup'), freshFetchInit({
        method: 'GET',
      }));
      const data = await parseJsonResponse(response);
      if (!response.ok) {
        throw new Error(data.message || '請求ロールアップの取得に失敗しました');
      }
      setBillingRollup(data.rollup || null);
      setBillingRollupError('');
      hasLoadedBillingRollup.current = true;
    } catch (err) {
      setBillingRollupError(err.message || '請求ロールアップの取得に失敗しました');
      if (!silent) {
        setBillingRollup(null);
      }
    } finally {
      if (!silent) {
        setBillingRollupLoading(false);
      }
    }
  }, [canManageLicenses]);

  useEffect(() => {
    fetchBillingRollup({ silent: false });
  }, [fetchBillingRollup]);

  useEffect(() => {
    if (!canManageLicenses) return;
    if (location.pathname !== '/licenses') return;
    if (!hasLoadedBillingRollup.current) return;
    fetchBillingRollup({ silent: true });
  }, [location.pathname, canManageLicenses, fetchBillingRollup]);

  useEffect(() => {
    if (!canManageLicenses) return;

    const onBillingDataChanged = () => {
      fetchBillingRollup({ silent: true });
    };
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible' && location.pathname === '/licenses') {
        fetchBillingRollup({ silent: true });
      }
    };

    window.addEventListener(BILLING_DATA_CHANGED_EVENT, onBillingDataChanged);
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => {
      window.removeEventListener(BILLING_DATA_CHANGED_EVENT, onBillingDataChanged);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [canManageLicenses, location.pathname, fetchBillingRollup]);

  const activeLicenseNames = useMemo(() => {
    if (!localLicenses) return [];
    if (Array.isArray(localLicenses)) {
      return localLicenses.map((license) => toLicenseName(license?.name)).filter(Boolean);
    }
    return Object.entries(localLicenses)
      .filter(([, value]) => value && value !== '')
      .map(([, name]) => toLicenseName(name));
  }, [localLicenses]);

  const hasAllLicense = activeLicenseNames.includes('all');

  const isModuleActive = (product) => {
    if (!product) return false;
    return isProductLicensed(product, activeLicenseNames, hasAllLicense);
  };

  const betaUsableModuleCount = useMemo(() => {
    if (!showBetaSummary || !canManageLicenses) return null;
    const nowMs = Date.now();
    let count = 0;
    for (const product of products) {
      if (product.isFree || product.isComingSoon) continue;
      const contract = findContractForProduct(product, contracts);
      if (!contract) continue;
      const status = contract.status || 'active';
      if (status === 'trial') {
        const trialEndAt = normalizeTimestamp(contract.trialEndAt);
        if (trialEndAt && isExpiryUsable(nowMs, trialEndAt.getTime())) count += 1;
      } else if (status === 'active') {
        if (isModuleActive(product)) count += 1;
      } else if (status === 'past_due') {
        if (isPastDueGraceActiveClient(contract, nowMs) && isModuleActive(product)) count += 1;
      }
    }
    return count;
  }, [showBetaSummary, canManageLicenses, products, contracts, activeLicenseNames, hasAllLicense]);

  const activeMonthlyTotal = billingRollup?.monthlyRecurringTotalExcludingTax ?? 0;

  const billableModuleCount = canManageLicenses
    ? (showBetaSummary && betaUsableModuleCount != null
      ? betaUsableModuleCount
      : (billingRollup?.billableModuleCount ?? 0))
    : activeLicenseNames.length;

  const showRollupSummaryLoading = canManageLicenses && (
    !isHubSettingsReady || (billingRollupLoading && !billingRollup)
  );

  const parseJsonResponse = async (response) => {
    const text = await response.text();
    if (!text) {
      return {};
    }
    try {
      return JSON.parse(text);
    } catch (err) {
      const error = new Error('データの読み込みに失敗しました。時間をおいて再度お試しください。');
      error.cause = err;
      throw error;
    }
  };

  const getContractByProduct = (product) => findContractForProduct(product, contracts);

  const countBillableCalendarDays = (fromMs, toMs) => {
    const diff = diffCalendarDaysJst(fromMs, toMs);
    return diff >= 0 ? diff + 1 : 0;
  };

  const getTrialInfo = (product) => {
    const contract = getContractByProduct(product);
    if (!contract || contract.status !== 'trial') return null;
    const trialEndAt = normalizeTimestamp(contract.trialEndAt);
    if (!trialEndAt || !isExpiryUsable(Date.now(), trialEndAt.getTime())) return null;
    const lastDayMs = lastUsageDayMsFromCutoff(trialEndAt.getTime());
    const remainingDays = countBillableCalendarDays(Date.now(), lastDayMs);
    const trialSource = contract.trialSource || null;
    return {
      trialEndAt: lastUsageDayDateFromCutoff(trialEndAt.getTime()),
      remainingDays,
      isFreeBenefit: isTrialFreeBenefit(contract),
      trialSource
    };
  };

  const getBillingDeferralInfo = (product) => {
    const contract = getContractByProduct(product);
    if (!contract || contract.status !== 'active') return null;
    const deferralEnd = normalizeTimestamp(contract.billingDeferralUntil);
    if (!deferralEnd || !isExpiryUsable(Date.now(), deferralEnd.getTime())) return null;
    const lastDayMs = lastUsageDayMsFromCutoff(deferralEnd.getTime());
    const remainingDays = countBillableCalendarDays(Date.now(), lastDayMs);
    return {
      deferralEnd: lastUsageDayDateFromCutoff(deferralEnd.getTime()),
      remainingDays,
      reason: contract.billingDeferralReason || null,
      isFreeBenefit: isDeferralFreeBenefit(contract)
    };
  };

  const getUsableUntil = (product) => {
    const contract = getContractByProduct(product);
    if (!contract) return null;

    if (contract.status === 'trial') {
      const trialEndAt = normalizeTimestamp(contract.trialEndAt);
      if (trialEndAt && isExpiryUsable(Date.now(), trialEndAt.getTime())) {
        const lastDayMs = lastUsageDayMsFromCutoff(trialEndAt.getTime());
        return {
          date: lastUsageDayDateFromCutoff(trialEndAt.getTime()),
          remainingDays: countBillableCalendarDays(Date.now(), lastDayMs),
          type: 'trial',
          description: '試用期間終了日'
        };
      }
    }

    if (contract.status === 'active') {
      const monthEndMs = cutoffMsForJstMonthEnd(Date.now());
      const lastDayMs = lastUsageDayMsFromCutoff(monthEndMs);
      return {
        date: new Date(lastDayMs),
        remainingDays: countBillableCalendarDays(Date.now(), lastDayMs),
        type: 'active',
        description: '当月末'
      };
    }

    return null;
  };

  // 再契約時の情報を取得（解約済みだが利用可能期間中の場合）
  // 解約後は期限の権威を usableUntil に一本化したため、これだけを見る。
  const getRecontractInfo = (product) => {
    const contract = getContractByProduct(product);
    if (!contract) return { canRecontract: false };

    if (contract.status === 'canceled') {
      const usableUntil = normalizeTimestamp(contract.usableUntil);
      if (usableUntil && isExpiryUsable(Date.now(), usableUntil.getTime())) {
        const lastDayMs = lastUsageDayMsFromCutoff(usableUntil.getTime());
        const remainingDays = countBillableCalendarDays(Date.now(), lastDayMs);
        return {
          canRecontract: true,
          noPaymentRequired: true,
          usableUntil: lastUsageDayDateFromCutoff(usableUntil.getTime()),
          remainingDays,
          type: contract.usableType || 'active',
          trialUsed: contract.trialUsed
        };
      }
    }

    return { canRecontract: false };
  };

  /** 試用終了後の再契約が必要（expired、または trial 期限経過） */
  const needsRecontractAfterTrialExpiry = (product) => {
    const contract = getContractByProduct(product);
    if (!contract) return false;
    if (contract.status === 'expired') return true;
    if (contract.status === 'trial') {
      const trialEndAt = normalizeTimestamp(contract.trialEndAt);
      if (trialEndAt && !isExpiryUsable(Date.now(), trialEndAt.getTime())) return true;
    }
    return false;
  };

  // 解約済みだが利用可能期間中の情報を取得
  // 解約時に usableUntil を必ず設定する運用としたため、このフィールドのみを信頼する。
  const getCanceledButUsableInfo = (product) => {
    const contract = getContractByProduct(product);
    if (!contract || contract.status !== 'canceled') return null;

    const usableUntil = normalizeTimestamp(contract.usableUntil);
    if (!usableUntil || !isExpiryUsable(Date.now(), usableUntil.getTime())) return null;

    const lastDayMs = lastUsageDayMsFromCutoff(usableUntil.getTime());
    const remainingDays = countBillableCalendarDays(Date.now(), lastDayMs);
    return {
      usableUntil: lastUsageDayDateFromCutoff(usableUntil.getTime()),
      remainingDays,
      type: contract.usableType || 'unknown'
    };
  };

  const getTrialSchedule = (product) => {
    const startDate = new Date();
    const startMs = startDate.getTime();
    const trialDays = resolveEffectiveTrialPeriodDays(product, {
      effectiveIsBeta,
      hubTrialPeriodDays: trialPeriodDays,
      hubBetaTrialPeriodDays: betaTrialPeriodDays,
    });
    const hasTrial = trialDays > 0;
    if (!hasTrial) {
      const monthEndMs = cutoffMsForJstMonthEnd(startMs);
      const prorationEndDate = lastUsageDayDateFromCutoff(monthEndMs);
      const nextBillingDate = getNextBillingAnchorDate(lastUsageDayMsFromCutoff(monthEndMs));
      const prorationDays = countBillableCalendarDays(startMs, lastUsageDayMsFromCutoff(monthEndMs));
      return {
        startDate,
        startMs,
        trialEnd: null,
        trialCutoffMs: null,
        nextBillingDate,
        prorationEndDate,
        prorationDays,
        trialDays: 0,
        hasTrial: false,
      };
    }
    const cutoffMs = cutoffMsFromStartAndDays(startMs, trialDays);
    const trialEnd = lastUsageDayDateFromCutoff(cutoffMs);
    const monthEndMs = cutoffMsForJstMonthEnd(cutoffMs);
    const prorationEndDate = lastUsageDayDateFromCutoff(monthEndMs);
    const nextBillingDate = getNextBillingAnchorDate(lastUsageDayMsFromCutoff(cutoffMs));
    const prorationDays = countBillableCalendarDays(cutoffMs, lastUsageDayMsFromCutoff(monthEndMs));
    return {
      startDate,
      startMs,
      trialEnd,
      trialCutoffMs: cutoffMs,
      nextBillingDate,
      prorationEndDate,
      prorationDays,
      trialDays,
      hasTrial: true,
    };
  };

  const handleOpenContract = (product) => {
    if (!canManageLicenses) return;
    setSelectedProduct(product);
    setActionError('');
    setPaymentMethodStatus(null);
    setShowConfirmModal(true);
    if (getProductPriceInfo(product).hasPrice) {
      fetch(buildApiUrl('/api/clinic/payment-info-status'), freshFetchInit({ method: 'GET' }))
        .then((res) => res.json())
        .then((data) => setPaymentMethodStatus({ hasPaymentMethod: data.hasPaymentMethod === true }))
        .catch(() => setPaymentMethodStatus({ hasPaymentMethod: false }));
    }
  };

  const handleCloseContract = () => {
    setShowConfirmModal(false);
    setSelectedProduct(null);
    setActionError('');
    setPaymentInfoRequired(false);
    setPaymentMethodStatus(null);
  };

  const handleConfirmContract = async (productParam) => {
    const productToUse = isProductRecord(productParam) ? productParam : selectedProduct;
    if (!productToUse) return;
    const contract = getContractByProduct(productToUse);
    let licenseNameForApi = getProductLicenseApiName(productToUse);
    if (!licenseNameForApi && contract?.licenseName) {
      licenseNameForApi = String(contract.licenseName).trim();
    }
    if (!licenseNameForApi) {
      setActionError('製品情報が不完全です。ページを再読み込みして再度お試しください。');
      showToast('license.contract.error', { message: '製品情報が不完全です。ページを再読み込みしてください。' });
      return;
    }
    const wasRecontract = needsRecontractAfterTrialExpiry(productToUse)
      || getRecontractInfo(productToUse).canRecontract;
    const moduleDisplayName = productToUse.name || productToUse.licenseName || licenseNameForApi;
    setSubmitting(true);
    setActionError('');
    setPaymentInfoRequired(false);
    const headers = { 'Content-Type': 'application/json', ...betaApiHeaders };
    try {
      const response = await fetch(buildApiUrl('/api/clinic/add-license'), {
        method: 'POST',
        headers,
        credentials: 'include',
        body: JSON.stringify({ licenseName: licenseNameForApi })
      });
      const data = await parseJsonResponse(response);
      if (!response.ok) {
        if (data.error === 'payment-info-required' || data.error === 'payment-incomplete') {
          setPaymentInfoRequired(true);
          const fallback = data.error === 'payment-incomplete'
            ? 'お支払いを完了できませんでした。支払い方法をご確認のうえ、再度お試しください。'
            : '有料ライセンスを契約するには、先に支払い方法を登録してください。';
          setActionError(data.message || fallback);
          showToast('license.contract.error', {
            message: data.message || fallback
          });
          return;
        }
        throw new Error(data.message || 'ライセンス追加に失敗しました');
      }
      setLocalLicenses(data.licenses || []);
      if (setLicenses) setLicenses(data.licenses || []);
      if (data.contract) {
        setContracts((prev) => {
          const next = prev.filter((item) => item.licenseName !== data.contract.licenseName);
          return [...next, data.contract];
        });
      }
      fetchBillingRollup({ silent: true });
      notifyBillingDataChanged();
      showToast(
        wasRecontract ? 'license.recontract.success' : 'license.contract.success',
        { moduleName: moduleDisplayName }
      );
      if (!productParam || !isProductRecord(productParam)) handleCloseContract();
    } catch (err) {
      setActionError(err.message || 'ライセンス追加に失敗しました');
      showToast('license.contract.error', { message: err.message || 'ライセンス追加に失敗しました' });
    } finally {
      setSubmitting(false);
    }
  };

  const handleContractClick = (product) => {
    if (!canManageLicenses) return;
    if (effectiveIsBeta) {
      handleConfirmContract(product);
    } else {
      handleOpenContract(product);
    }
  };

  const handleOpenCancelModal = (product) => {
    if (!canManageLicenses) return;
    setCancelTargetProduct(product);
    setActionError('');
    setShowCancelModal(true);
  };

  const handleCloseCancelModal = () => {
    setShowCancelModal(false);
    setCancelTargetProduct(null);
    setCancelModalStep(1);
    setActionError('');
    setCancelReasons([]);
    setCancelFreeComment('');
  };

  // 1段階目から2段階目（理由入力）へ進む
  const handleProceedToReasonForm = () => {
    setCancelModalStep(2);
    setActionError('');
  };

  const handleConfirmCancel = async () => {
    if (!cancelTargetProduct) return;
    const targetContract = getContractByProduct(cancelTargetProduct);
    setSubmitting(true);
    setActionError('');
    try {
      const licenseNameForApi = getProductLicenseApiName(cancelTargetProduct);
      if (!licenseNameForApi) {
        throw new Error('製品にライセンスキーが設定されていません');
      }

      const response = await fetch(buildApiUrl('/api/clinic/cancel-license'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          licenseName: licenseNameForApi,
          // 解約理由情報
          cancelReasons: cancelReasons,
          cancelComment: cancelFreeComment,
          productName: cancelTargetProduct.name
        })
      });
      const data = await parseJsonResponse(response);
      if (!response.ok) {
        throw new Error(data.message || 'ライセンス解約に失敗しました');
      }
      setLocalLicenses(data.licenses || []);
      if (setLicenses) setLicenses(data.licenses || []);
      const canceledContractLicenseName =
        data.contract?.licenseName ?? targetContract?.licenseName ?? licenseNameForApi;
      setContracts((prev) => prev.map((contract) => {
        if (contract.licenseName !== canceledContractLicenseName) return contract;
        const canceled = data.contract || {};
        return {
          ...contract,
          status: 'canceled',
          usableUntil: canceled.usableUntil ?? data.usableUntil ?? null,
          usableType: canceled.usableType ?? data.usableType ?? null,
          trialEndAt: null,
          billingDeferralUntil: null,
          billingDeferralReason: null,
        };
      }));
      fetchBillingRollup({ silent: true });
      notifyBillingDataChanged();
      showToast('license.cancel.success', {
        moduleName: cancelTargetProduct.name || cancelTargetProduct.licenseName || licenseNameForApi
      });
      handleCloseCancelModal();
    } catch (err) {
      setActionError(err.message || 'ライセンス解約に失敗しました');
      showToast('license.cancel.error', { message: err.message || 'ライセンス解約に失敗しました' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <div className="hub-main-header">
        <h1 className="hub-page-title">ライセンス管理</h1>
        {betaFeaturesRestricted ? (
          <span
            className="hub-btn hub-btn-secondary"
            style={{ opacity: 0.6, pointerEvents: 'none', cursor: 'not-allowed' }}
            title="β版期間中はご利用いただけません"
          >
            請求・決済を見る
          </span>
        ) : (
          <Link to="/billing" className="hub-btn hub-btn-secondary">
            請求・決済を見る
          </Link>
        )}
      </div>

      <div className="hub-main-content">
        {!canManageLicenses && (
          <div style={{
            background: 'var(--hub-warning-light)',
            padding: 'var(--hub-space-md)',
            borderRadius: 'var(--hub-radius-md)',
            marginBottom: 'var(--hub-space-xl)',
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--hub-space-sm)'
          }}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ width: 20, height: 20, color: 'var(--hub-warning)' }}>
              <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
              <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
            </svg>
            <span style={{ color: 'var(--hub-text-primary)' }}>
              ライセンスの追加・変更には医師または管理者権限が必要です。
            </span>
          </div>
        )}

        {/* 契約中ライセンスサマリー */}
        <div 
          className="hub-card hub-enter-panel animate-fade-in-up stagger-1"
          data-panel-index="0"
          style={{ 
            background: 'var(--hub-bg-secondary)',
            marginBottom: 'var(--hub-space-2xl)'
          }}
        >
          {showRollupSummaryLoading ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: 'var(--hub-space-lg)' }}>
              <LoadingSpinner />
            </div>
          ) : (
          <div className="hub-license-summary" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 'var(--hub-space-xl)' }}>
            <div>
              <div style={{ 
                fontSize: '0.8125rem',
                color: 'var(--hub-text-muted)',
                marginBottom: 'var(--hub-space-xs)'
              }}>
                {showBetaSummary ? '利用中' : '契約中（課金対象）'}
              </div>
              <div style={{ 
                fontFamily: 'var(--hub-font-display)',
                fontSize: '1.75rem',
                fontWeight: 800,
                color: 'var(--hub-text-primary)'
              }}>
                {billableModuleCount}<span style={{ fontSize: '0.875rem', fontWeight: 500, color: 'var(--hub-text-muted)', marginLeft: '4px' }}>モジュール</span>
              </div>
              {canManageLicenses && !showBetaSummary && billingRollup?.canceledUsableModuleCount > 0 && (
                <div style={{ fontSize: '0.75rem', color: 'var(--hub-text-muted)', marginTop: '4px' }}>
                  解約済（利用可）{billingRollup.canceledUsableModuleCount} モジュール
                </div>
              )}
              {canManageLicenses && !showBetaSummary && billingRollup?.pendingBillingSetupItems?.length > 0 && (
                <div style={{
                  fontSize: '0.75rem',
                  color: 'var(--hub-text-muted)',
                  marginTop: 'var(--hub-space-sm)',
                  lineHeight: 1.5,
                }}>
                  {billingRollup.pendingBillingSetupItems.map((item) => (
                    <div key={item.licenseName || item.moduleName}>
                      {item.moduleName}：請求スケジュール未確定（試用終了 {item.trialEndLabel}）
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div style={{ 
              fontSize: '0.875rem',
              color: 'var(--hub-text-secondary)',
              textAlign: 'right'
            }}>
              <div style={{ color: 'var(--hub-text-muted)', marginBottom: '4px' }}>
                {showBetaSummary ? '月額（β版）' : '月額合計（定額・税別）'}
              </div>
              <div style={{ fontWeight: 700, color: 'var(--hub-text-primary)' }}>
                {canManageLicenses
                  ? (showBetaSummary ? 'β版無料' : `${formatYen(activeMonthlyTotal)}（税別）`)
                  : '—'}
              </div>
              {showBetaSummary && canManageLicenses && (
                <div style={{ fontSize: '0.75rem', color: 'var(--hub-text-muted)', marginTop: '4px' }}>
                  β版期間中は課金は発生しません
                </div>
              )}
              {billingRollupError && billingRollup && (
                <div style={{ fontSize: '0.75rem', color: 'var(--hub-danger)', marginTop: '4px' }}>
                  {billingRollupError}（表示は最後に取得できた内容です）
                </div>
              )}
              {billingRollupError && !billingRollup && (
                <div style={{ fontSize: '0.75rem', color: 'var(--hub-danger)', marginTop: '4px' }}>
                  {billingRollupError}
                </div>
              )}
            </div>
          </div>
          )}
        </div>

        {/* モジュール別ライセンス */}
        <section className="hub-enter-panel animate-fade-in-up stagger-2" data-panel-index="1" style={{ marginBottom: 'var(--hub-space-3xl)' }}>
          <h2 style={{ 
            fontFamily: 'var(--hub-font-display)',
            fontSize: '1.25rem',
            fontWeight: 700,
            marginBottom: 'var(--hub-space-lg)'
          }}>
            モジュール別ライセンス
          </h2>

          {productsLoading || contractsLoading ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: 'var(--hub-space-2xl)' }}>
              <LoadingSpinner size="large" />
            </div>
          ) : productsError || contractsError ? (
            <div style={{ color: 'var(--hub-text-secondary)' }}>
              {productsError || contractsError}
            </div>
          ) : (
            <div style={{ 
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
              gap: 'var(--hub-space-md)'
            }}>
              {products.map((product) => {
                const priceInfo = getProductPriceInfo(product);
                const active = isModuleActive(product);
                return (
                  <div key={product.id} className="hub-card" style={{ display: 'flex', flexDirection: 'column' }}>
                    <div style={{ 
                      display: 'flex',
                      justifyContent: 'flex-end',
                      marginBottom: 'var(--hub-space-md)'
                    }}>
                      {(() => {
                        const contract = getContractByProduct(product);
                        const trial = getTrialInfo(product);
                        const deferral = getBillingDeferralInfo(product);
                        const canceledUsable = getCanceledButUsableInfo(product);

                        // 決済失敗（past_due）— 猶予内
                        if (contract?.status === 'past_due') {
                          const graceUntil = normalizeTimestamp(contract.pastDueGraceUntil);
                          const nowMs = Date.now();
                          const inGrace = graceUntil && graceUntil > nowMs;
                          const remainingDays = inGrace
                            ? Math.max(0, Math.ceil((graceUntil - nowMs) / (1000 * 60 * 60 * 24)))
                            : 0;
                          const label = inGrace
                            ? `決済未完了（猶予${remainingDays}日）`
                            : '決済未完了';
                          return (
                            <span style={{
                              background: 'rgba(239, 68, 68, 0.12)',
                              color: '#b91c1c',
                              padding: '4px 10px',
                              borderRadius: 'var(--hub-radius-full)',
                              fontSize: '0.75rem',
                              fontWeight: 600,
                              whiteSpace: 'nowrap'
                            }}>
                              {label}
                            </span>
                          );
                        }

                        // expired ステータス（試用期間終了 + 自動移行失敗）
                        if (contract?.status === 'expired') {
                          return (
                            <span style={{
                              background: 'rgba(239, 68, 68, 0.1)',
                              color: '#dc2626',
                              padding: '4px 10px',
                              borderRadius: 'var(--hub-radius-full)',
                              fontSize: '0.75rem',
                              fontWeight: 600,
                              whiteSpace: 'nowrap'
                            }}>
                              試用期間終了
                            </span>
                          );
                        }

                        if (trial) {
                          // 試用残り0日（期限切れだがバッチ未処理）
                          if (trial.remainingDays === 0) {
                            return (
                              <span style={{
                                background: 'rgba(245, 158, 11, 0.15)',
                                color: '#b45309',
                                padding: '4px 10px',
                                borderRadius: 'var(--hub-radius-full)',
                                fontSize: '0.75rem',
                                fontWeight: 600,
                                whiteSpace: 'nowrap'
                              }}>
                                {trial.isFreeBenefit ? '特典期間終了' : '試用期間終了'}
                              </span>
                            );
                          }
                          // 招待特典で新規付与された trial は「特典」ラベルで表示
                          const label = trial.isFreeBenefit
                            ? `特典による無料期間（残り${trial.remainingDays}日）`
                            : `試用中（残り${trial.remainingDays}日）`;
                          const badgeBg = trial.isFreeBenefit ? 'rgba(59, 130, 246, 0.1)' : 'var(--hub-success-light)';
                          const badgeColor = trial.isFreeBenefit ? '#3b82f6' : 'var(--hub-success)';
                          return (
                            <span style={{
                              background: badgeBg,
                              color: badgeColor,
                              padding: '4px 10px',
                              borderRadius: 'var(--hub-radius-full)',
                              fontSize: '0.75rem',
                              fontWeight: 600,
                              whiteSpace: 'nowrap'
                            }}>
                              {label}
                            </span>
                          );
                        }

                        // 本契約中の一時的支払猶予（招待特典など）
                        if (deferral) {
                          const dateStr = deferral.deferralEnd.toLocaleDateString('ja-JP', { month: 'long', day: 'numeric' });
                          const label = deferral.isFreeBenefit
                            ? `特典による無料期間（${dateStr}まで）`
                            : `支払猶予中（${dateStr}まで）`;
                          return (
                            <span style={{
                              background: 'rgba(59, 130, 246, 0.1)',
                              color: '#3b82f6',
                              padding: '4px 10px',
                              borderRadius: 'var(--hub-radius-full)',
                              fontSize: '0.75rem',
                              fontWeight: 600,
                              whiteSpace: 'nowrap'
                            }}>
                              {label}
                            </span>
                          );
                        }

                        if (canceledUsable) {
                          return (
                            <span style={{
                              background: 'rgba(245, 158, 11, 0.1)',
                              color: '#b45309',
                              padding: '4px 10px',
                              borderRadius: 'var(--hub-radius-full)',
                              fontSize: '0.75rem',
                              fontWeight: 600,
                              whiteSpace: 'nowrap'
                            }}>
                              解約済（{canceledUsable.usableUntil.toLocaleDateString()}まで利用可）
                            </span>
                          );
                        }
                        
                        return (
                          <span style={{
                            background: active ? 'var(--hub-success-light)' : 'var(--hub-bg-secondary)',
                            color: active ? 'var(--hub-success)' : 'var(--hub-text-muted)',
                            padding: '4px 10px',
                            borderRadius: 'var(--hub-radius-full)',
                            fontSize: '0.75rem',
                            fontWeight: 600,
                            whiteSpace: 'nowrap'
                          }}>
                            {active ? '契約中' : '未契約'}
                          </span>
                        );
                      })()}
                    </div>

                    <h3 style={{ fontWeight: 700, marginBottom: '4px' }}>
                      {product.name}
                    </h3>
                    <p style={{ 
                      color: 'var(--hub-text-muted)',
                      fontSize: '0.8125rem',
                      margin: 0
                    }}>
                      {product.shortDescription}
                    </p>
                    <div style={{
                      marginTop: 'var(--hub-space-md)',
                      fontWeight: 700,
                      lineHeight: 1.4,
                      wordBreak: 'break-word'
                    }}>
                      <ProductPriceLabel product={product} />
                    </div>
                    {(() => {
                      const trial = getTrialInfo(product);
                      if (trial) {
                        return (
                          <div style={{ marginTop: '4px', fontSize: '0.75rem', color: 'var(--hub-text-muted)' }}>
                            {trial.isFreeBenefit ? '特典終了日' : '試用終了日'}: {trial.trialEndAt.toLocaleDateString()}
                          </div>
                        );
                      }
                      const deferral = getBillingDeferralInfo(product);
                      if (deferral) {
                        return (
                          <div style={{ marginTop: '4px', fontSize: '0.75rem', color: 'var(--hub-text-muted)' }}>
                            {deferral.isFreeBenefit ? '特典終了日' : '支払猶予終了日'}: {deferral.deferralEnd.toLocaleDateString()}
                          </div>
                        );
                      }
                      return null;
                    })()}

                    <div style={{ marginTop: 'auto', paddingTop: 'var(--hub-space-md)' }}>
                      {(() => {
                        const canceledUsable = getCanceledButUsableInfo(product);
                        
                        if (product.isComingSoon) {
                          return (
                            <button className="hub-btn hub-btn-secondary" style={{ width: '100%' }} disabled>
                              準備中
                            </button>
                          );
                        }

                        // expired: 支払い方法登録を促して再契約
                        if (needsRecontractAfterTrialExpiry(product)) {
                          if (paymentMethodLoading) {
                            return (
                              <button className="hub-btn hub-btn-primary" style={{ width: '100%' }} disabled>
                                <LoadingSpinner size="small" />
                              </button>
                            );
                          }
                          if (hasPaymentMethod) {
                            return (
                              <button
                                className="hub-btn hub-btn-primary"
                                style={{ width: '100%' }}
                                onClick={() => handleContractClick(product)}
                                disabled={!canManageLicenses || submitting}
                              >
                                再契約する
                              </button>
                            );
                          }
                          if (!canManageLicenses) {
                            return (
                              <button className="hub-btn hub-btn-primary" style={{ width: '100%' }} disabled>
                                支払い方法を登録して再契約
                              </button>
                            );
                          }
                          return (
                            <Link
                              to="/billing"
                              className="hub-btn hub-btn-primary"
                              style={{ width: '100%', textAlign: 'center', textDecoration: 'none' }}
                            >
                              支払い方法を登録して再契約
                            </Link>
                          );
                        }
                        
                        // 解約済みだが利用可能期間中の場合
                        if (canceledUsable) {
                          return (
                            <button
                              className="hub-btn hub-btn-primary"
                              style={{ width: '100%' }}
                              onClick={() => handleContractClick(product)}
                              disabled={!canManageLicenses || submitting}
                            >
                              再契約する
                            </button>
                          );
                        }
                        
                        if (active && !needsRecontractAfterTrialExpiry(product)) {
                          return (
                            <button
                              className="hub-btn hub-btn-secondary"
                              style={{ width: '100%' }}
                              onClick={() => handleOpenCancelModal(product)}
                              disabled={!canManageLicenses || submitting}
                            >
                              解約する
                            </button>
                          );
                        }
                        
                        return (
                          <button
                            className="hub-btn hub-btn-primary"
                            style={{ width: '100%' }}
                            onClick={() => handleContractClick(product)}
                            disabled={!canManageLicenses || submitting}
                          >
                            ライセンスを契約
                          </button>
                        );
                      })()}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </section>

        {/* 補足情報 */}
        <section style={{ marginTop: 'var(--hub-space-xl)' }}>
          <p style={{ 
            fontSize: '0.75rem',
            color: 'var(--hub-text-muted)',
            margin: 0,
            lineHeight: 1.7
          }}>
            ※ ライセンスはクリニック単位で管理されます。追加・変更には医師または管理者権限が必要です。
          </p>
        </section>

        {/* 契約確認モーダル */}
        {showConfirmModal && selectedProduct && (
          <div 
            className="hub-modal-overlay"
            style={{
              top: 'var(--hub-navbar-height, 64px)',
              padding: '24px'
            }}
            onClick={handleCloseContract}
          >
            <div 
              className="hub-card"
              style={{ 
                width: '100%',
                maxWidth: '520px',
                maxHeight: 'calc(100vh - var(--hub-navbar-height, 64px) - 48px)',
                overflow: 'auto',
                background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(255, 255, 255, 0.75) 50%, rgba(255, 255, 255, 0.85) 100%)'
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <div style={{ 
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: 'var(--hub-space-lg)'
              }}>
                <h2 style={{ 
                  fontFamily: 'var(--hub-font-display)',
                  fontSize: '1.25rem',
                  fontWeight: 700,
                  margin: 0
                }}>
                  ライセンス契約の確認
                </h2>
                <button onClick={handleCloseContract} className="hub-btn hub-btn-ghost" style={{ padding: '8px' }}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ width: 20, height: 20 }}>
                    <line x1="18" y1="6" x2="6" y2="18"/>
                    <line x1="6" y1="6" x2="18" y2="18"/>
                  </svg>
                </button>
              </div>

              {(() => {
                const priceInfo = getProductPriceInfo(selectedProduct);
                const {
                  trialEnd,
                  trialCutoffMs,
                  startMs,
                  nextBillingDate,
                  prorationEndDate,
                  prorationDays,
                  trialDays,
                  hasTrial,
                } = getTrialSchedule(selectedProduct);
                const trialPeriodNote = describeTrialPeriodSource(selectedProduct, {
                  effectiveIsBeta,
                });
                const recontractInfo = getRecontractInfo(selectedProduct);
                const contract = getContractByProduct(selectedProduct);
                const hasUsedTrial = (contract?.trialUsed || false) && !(recontractInfo.canRecontract && recontractInfo.type === 'trial');
                const showTrialStep = !hasUsedTrial && hasTrial;
                const immediateBilling = hasUsedTrial || !hasTrial;
                const prorationChargeStartMs = immediateBilling ? startMs : (trialCutoffMs ?? startMs);
                const proratedAmount = priceInfo.hasPrice
                  ? estimateProratedAmount(priceInfo.monthlyPrice, prorationChargeStartMs)
                  : 0;
                const prorationStartLabel = immediateBilling
                  ? '契約日'
                  : trialEnd?.toLocaleDateString();
                const nextMonthlyTotal = activeMonthlyTotal + (priceInfo.hasPrice ? priceInfo.monthlyPrice : 0);
                
                return (
                  <>
                    {/* 契約モジュール情報 */}
                    <div style={{ 
                      padding: 'var(--hub-space-md)',
                      background: 'var(--hub-bg-secondary)',
                      borderRadius: 'var(--hub-radius-md)',
                      marginBottom: 'var(--hub-space-xl)'
                    }}>
                      <div style={{ fontWeight: 700, fontSize: '1rem', marginBottom: '4px' }}>{selectedProduct.name}</div>
                      <div style={{
                        color: 'var(--hub-text-secondary)',
                        fontSize: '0.875rem',
                        lineHeight: 1.45,
                        wordBreak: 'break-word'
                      }}>
                        <ProductPriceLabel product={selectedProduct} prefix="月額 " />
                      </div>
                    </div>

                    {/* 再契約で利用可能期間中の場合 */}
                    {recontractInfo.canRecontract && recontractInfo.noPaymentRequired && (
                      <div style={{ 
                        padding: 'var(--hub-space-md)',
                        background: 'rgba(34, 197, 94, 0.08)',
                        borderRadius: 'var(--hub-radius-md)',
                        marginBottom: 'var(--hub-space-lg)',
                        border: '1px solid rgba(34, 197, 94, 0.2)'
                      }}>
                        <div style={{ 
                          display: 'flex', 
                          alignItems: 'flex-start', 
                          gap: 'var(--hub-space-sm)'
                        }}>
                          <svg viewBox="0 0 24 24" fill="none" stroke="#22c55e" strokeWidth="2" style={{ width: 20, height: 20, flexShrink: 0, marginTop: '2px' }}>
                            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                            <polyline points="22 4 12 14.01 9 11.01"/>
                          </svg>
                          <div style={{ fontSize: '0.875rem', color: 'var(--hub-text-primary)', lineHeight: 1.6 }}>
                            <strong>再契約時の追加支払いはありません</strong><br />
                            <span style={{ color: 'var(--hub-text-secondary)' }}>
                              {recontractInfo.type === 'trial'
                                ? `試用期間終了日（${recontractInfo.usableUntil.toLocaleDateString()}）まで引き続きご利用いただけます。`
                                : `当月末（${recontractInfo.usableUntil.toLocaleDateString()}）まで引き続きご利用いただけます。お支払い済みの期間のため追加のお支払いは発生しません。`
                              }
                            </span>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* 通常の新規契約フロー（再契約で期間内でない場合、または新規の場合） */}
                    {!recontractInfo.noPaymentRequired && (
                      <>
                        {/* スケジュール */}
                        <div style={{ marginBottom: 'var(--hub-space-xl)' }}>
                          <h4 style={{ 
                            fontSize: '0.8125rem', 
                            fontWeight: 600, 
                            color: 'var(--hub-text-muted)',
                            marginBottom: 'var(--hub-space-md)',
                            textTransform: 'uppercase',
                            letterSpacing: '0.05em'
                          }}>
                            ご契約の流れ
                          </h4>
                          
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--hub-space-md)' }}>
                            {/* Step 1: 試用期間（未使用かつ試用ありの場合のみ表示） */}
                            {showTrialStep && (
                              <div style={{ 
                                display: 'flex',
                                alignItems: 'flex-start',
                                gap: 'var(--hub-space-md)'
                              }}>
                                <div style={{
                                  width: '24px',
                                  height: '24px',
                                  borderRadius: '50%',
                                  background: 'var(--hub-success-light)',
                                  color: 'var(--hub-success)',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  fontSize: '0.75rem',
                                  fontWeight: 700,
                                  flexShrink: 0
                                }}>1</div>
                                <div style={{ flex: 1 }}>
                                  <div style={{ fontWeight: 600, fontSize: '0.875rem', marginBottom: '2px' }}>
                                    無料試用期間
                                  </div>
                                  <div style={{ fontSize: '0.8125rem', color: 'var(--hub-text-secondary)', lineHeight: 1.5 }}>
                                    本日から{trialDays}日間、無料でお試しいただけます。<br />
                                    <span style={{ color: 'var(--hub-text-muted)' }}>試用終了日：{trialEnd.toLocaleDateString()}</span>
                                    {trialPeriodNote ? (
                                      <>
                                        <br />
                                        <span style={{ color: 'var(--hub-text-muted)', fontSize: '0.75rem' }}>{trialPeriodNote}</span>
                                      </>
                                    ) : null}
                                  </div>
                                </div>
                              </div>
                            )}

                            {/* 試用済み、または試用なしの場合の注意 */}
                            {immediateBilling && priceInfo.hasPrice && (
                              <div style={{ 
                                padding: 'var(--hub-space-sm) var(--hub-space-md)',
                                background: 'rgba(245, 158, 11, 0.08)',
                                borderRadius: 'var(--hub-radius-md)',
                                marginBottom: 'var(--hub-space-sm)',
                                border: '1px solid rgba(245, 158, 11, 0.2)'
                              }}>
                                <div style={{ fontSize: '0.8125rem', color: '#92400e', lineHeight: 1.5 }}>
                                  {hasUsedTrial
                                    ? '※ 試用期間は既にご利用済みのため、契約後すぐにお支払いが発生します。'
                                    : `※ ${trialPeriodNote}`}
                                </div>
                              </div>
                            )}

                            {/* Step 2: 初回決済 */}
                            <div style={{ 
                              display: 'flex',
                              alignItems: 'flex-start',
                              gap: 'var(--hub-space-md)'
                            }}>
                              <div style={{
                                width: '24px',
                                height: '24px',
                                borderRadius: '50%',
                                background: 'var(--hub-bg-tertiary)',
                                color: 'var(--hub-text-secondary)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: '0.75rem',
                                fontWeight: 700,
                                flexShrink: 0
                              }}>{immediateBilling ? '1' : '2'}</div>
                              <div style={{ flex: 1 }}>
                                <div style={{ fontWeight: 600, fontSize: '0.875rem', marginBottom: '2px' }}>
                                  {immediateBilling ? '初回決済' : '初回決済（日割り）'}
                                </div>
                                {priceInfo.hasPrice ? (
                                  <div style={{ fontSize: '0.8125rem', color: 'var(--hub-text-secondary)', lineHeight: 1.5 }}>
                                    {immediateBilling
                                      ? <>契約後すぐに月末までの日割りでお支払い<br /></>
                                      : <>試用終了後、月末までの{prorationDays}日分を日割りでお支払い<br /></>
                                    }
                                    <span style={{ fontWeight: 600, color: 'var(--hub-text-primary)' }}>{formatYen(proratedAmount)}（税別）</span>
                                    <span style={{ color: 'var(--hub-text-muted)' }}>（{prorationStartLabel} 〜 {prorationEndDate.toLocaleDateString()}）</span>
                                  </div>
                                ) : (
                                  <div style={{ fontSize: '0.8125rem', color: 'var(--hub-text-muted)' }}>
                                    無料モジュールのため請求はありません
                                  </div>
                                )}
                              </div>
                            </div>

                            {/* Step 3: 翌月以降 */}
                            <div style={{ 
                              display: 'flex',
                              alignItems: 'flex-start',
                              gap: 'var(--hub-space-md)'
                            }}>
                              <div style={{
                                width: '24px',
                                height: '24px',
                                borderRadius: '50%',
                                background: 'var(--hub-bg-tertiary)',
                                color: 'var(--hub-text-secondary)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: '0.75rem',
                                fontWeight: 700,
                                flexShrink: 0
                              }}>{immediateBilling ? '2' : '3'}</div>
                              <div style={{ flex: 1 }}>
                                <div style={{ fontWeight: 600, fontSize: '0.875rem', marginBottom: '2px' }}>
                                  翌月以降の請求
                                </div>
                                <div style={{ fontSize: '0.8125rem', color: 'var(--hub-text-secondary)', lineHeight: 1.5 }}>
                                  {nextBillingDate.toLocaleDateString()}から、毎月1日にまとめて請求<br />
                                  <span style={{ fontWeight: 600, color: 'var(--hub-text-primary)' }}>{formatYen(nextMonthlyTotal)}</span>
                                  <span style={{ color: 'var(--hub-text-muted)' }}>/月（税別・他モジュールとの合計）</span>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* 注意書き（通常フロー） */}
                        <div style={{ 
                          fontSize: '0.75rem', 
                          color: 'var(--hub-text-muted)',
                          lineHeight: 1.6,
                          padding: 'var(--hub-space-sm) 0',
                          borderTop: '1px solid var(--hub-border-light)'
                        }}>
                          {hasUsedTrial 
                            ? '契約後すぐにお支払いが発生します。解約後も当月末まではご利用いただけます。'
                            : '試用期間中はいつでも解約でき、請求は発生しません。'
                          }
                        </div>
                      </>
                    )}

                    {/* 再契約で利用可能期間中の場合の注意書き */}
                    {recontractInfo.noPaymentRequired && (
                      <div style={{ 
                        fontSize: '0.75rem', 
                        color: 'var(--hub-text-muted)',
                        lineHeight: 1.6,
                        padding: 'var(--hub-space-sm) 0',
                        borderTop: '1px solid var(--hub-border-light)'
                      }}>
                        {recontractInfo.type === 'trial'
                          ? '試用期間は既に開始されているため、残り日数のみご利用いただけます。解約中も試用期間は経過します。'
                          : '当月末まで引き続きご利用いただけます。翌月からは通常の月額料金が発生します。'
                        }
                      </div>
                    )}
                  </>
                );
              })()}

              {actionError && (
                <div style={{ marginTop: 'var(--hub-space-md)', color: 'var(--hub-danger)' }}>
                  {actionError}
                  {paymentInfoRequired && (
                    <div style={{ marginTop: 'var(--hub-space-sm)' }}>
                      <Link to="/billing" className="hub-btn hub-btn-primary" style={{ display: 'inline-block' }}>
                        決済・請求ページで支払い方法を確認する
                      </Link>
                    </div>
                  )}
                </div>
              )}

              {getProductPriceInfo(selectedProduct).hasPrice && paymentMethodStatus?.hasPaymentMethod === false && (
                <div style={{
                  padding: 'var(--hub-space-md)',
                  background: 'rgba(245, 158, 11, 0.08)',
                  borderRadius: 'var(--hub-radius-md)',
                  marginTop: 'var(--hub-space-md)',
                  marginBottom: 'var(--hub-space-sm)',
                  border: '1px solid rgba(245, 158, 11, 0.2)'
                }}>
                  <p style={{ margin: 0, fontSize: '0.875rem', color: 'var(--hub-text-primary)', lineHeight: 1.6 }}>
                    有料ライセンスを契約するには、先に支払い方法の登録が必要です。
                  </p>
                </div>
              )}

              <div style={{ display: 'flex', gap: 'var(--hub-space-md)', marginTop: 'var(--hub-space-xl)' }}>
                <button className="hub-btn hub-btn-secondary" style={{ flex: 1 }} onClick={handleCloseContract}>
                  キャンセル
                </button>
                {getProductPriceInfo(selectedProduct).hasPrice && paymentMethodStatus === null ? (
                  <button className="hub-btn hub-btn-primary" style={{ flex: 1 }} disabled>
                    <LoadingSpinner size="small" />
                  </button>
                ) : getProductPriceInfo(selectedProduct).hasPrice && paymentMethodStatus?.hasPaymentMethod === false ? (
                  <Link
                    to="/billing"
                    className="hub-btn hub-btn-primary"
                    style={{ flex: 1, textAlign: 'center', textDecoration: 'none' }}
                  >
                    支払方法を登録
                  </Link>
                ) : (
                  <button
                    className="hub-btn hub-btn-primary"
                    style={{ flex: 1 }}
                    onClick={() => handleConfirmContract()}
                    disabled={!canManageLicenses || submitting}
                  >
                    {submitting ? <LoadingSpinner size="small" /> : (
                      getRecontractInfo(selectedProduct).canRecontract ? '再契約する' : '契約する'
                    )}
                  </button>
                )}
              </div>
            </div>
          </div>
        )}

        {/* 解約モーダル（2段階） */}
        {showCancelModal && cancelTargetProduct && (
          <div 
            className="hub-modal-overlay"
            style={{
              top: 'var(--hub-navbar-height, 64px)',
              padding: '24px'
            }}
          >
            <div 
              className="hub-card"
              style={{ 
                width: '100%',
                maxWidth: '480px',
                maxHeight: 'calc(100vh - var(--hub-navbar-height, 64px) - 48px)',
                overflow: 'auto',
                background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(255, 255, 255, 0.75) 50%, rgba(255, 255, 255, 0.85) 100%)'
              }}
            >
              {/* ===== STEP 1: 解約確認画面 ===== */}
              {cancelModalStep === 1 && (
                <>
                  <div style={{ 
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: 'var(--hub-space-lg)'
                  }}>
                    <h2 style={{ 
                      fontFamily: 'var(--hub-font-display)',
                      fontSize: '1.25rem',
                      fontWeight: 700,
                      margin: 0,
                      color: 'var(--hub-danger)'
                    }}>
                      ライセンス解約の確認
                    </h2>
                    <button onClick={handleCloseCancelModal} className="hub-btn hub-btn-ghost" style={{ padding: '8px' }}>
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ width: 20, height: 20 }}>
                        <line x1="18" y1="6" x2="6" y2="18"/>
                        <line x1="6" y1="6" x2="18" y2="18"/>
                      </svg>
                    </button>
                  </div>

                  {/* 解約対象モジュール情報 */}
                  <div style={{ 
                    padding: 'var(--hub-space-md)',
                    background: 'var(--hub-bg-secondary)',
                    borderRadius: 'var(--hub-radius-md)',
                    marginBottom: 'var(--hub-space-lg)',
                    border: '1px solid var(--hub-danger)',
                    borderColor: 'rgba(239, 68, 68, 0.3)'
                  }}>
                    <div style={{ fontWeight: 700, fontSize: '1rem', marginBottom: '4px' }}>{cancelTargetProduct.name}</div>
                    <div style={{
                      color: 'var(--hub-text-secondary)',
                      fontSize: '0.875rem',
                      lineHeight: 1.45,
                      wordBreak: 'break-word'
                    }}>
                      <ProductPriceLabel product={cancelTargetProduct} prefix="月額 " />
                    </div>
                  </div>

                  {/* 利用可能期間の説明 */}
                  {(() => {
                    const usableInfo = getUsableUntil(cancelTargetProduct);
                    if (!usableInfo) return null;
                    
                    return (
                      <div style={{ 
                        padding: 'var(--hub-space-md)',
                        background: 'rgba(59, 130, 246, 0.08)',
                        borderRadius: 'var(--hub-radius-md)',
                        marginBottom: 'var(--hub-space-md)',
                        border: '1px solid rgba(59, 130, 246, 0.2)'
                      }}>
                        <div style={{ 
                          display: 'flex', 
                          alignItems: 'flex-start', 
                          gap: 'var(--hub-space-sm)'
                        }}>
                          <svg viewBox="0 0 24 24" fill="none" stroke="#3b82f6" strokeWidth="2" style={{ width: 20, height: 20, flexShrink: 0, marginTop: '2px' }}>
                            <circle cx="12" cy="12" r="9"/>
                            <polyline points="12 6 12 12 16 14"/>
                          </svg>
                          <div style={{ fontSize: '0.875rem', color: 'var(--hub-text-primary)', lineHeight: 1.6 }}>
                            <strong>解約後も {usableInfo.date.toLocaleDateString()} まで利用可能</strong><br />
                            <span style={{ color: 'var(--hub-text-secondary)' }}>
                              {usableInfo.type === 'trial' 
                                ? '試用期間終了日まで引き続きご利用いただけます。' 
                                : '当月のお支払いが済んでいるため、月末まで引き続きご利用いただけます。'}
                            </span>
                          </div>
                        </div>
                      </div>
                    );
                  })()}

                  {/* 注意事項 */}
                  <div style={{ 
                    fontSize: '0.8125rem', 
                    color: 'var(--hub-text-secondary)',
                    lineHeight: 1.7,
                    marginBottom: 'var(--hub-space-lg)'
                  }}>
                    <ul style={{ margin: 0, paddingLeft: '1.25rem' }}>
                      {(() => {
                        const usableInfo = getUsableUntil(cancelTargetProduct);
                        if (usableInfo?.type === 'trial') {
                          return (
                            <>
                              <li>試用終了日（{usableInfo.date.toLocaleDateString()}）まではご利用いただけます</li>
                              <li>利用可能期間中に再契約すれば、そのまま継続利用できます</li>
                              <li>解約中も試用期間は経過します（期間の一時停止はありません）</li>
                            </>
                          );
                        }
                        return (
                          <>
                            <li>当月末（{usableInfo?.date.toLocaleDateString()}）までは引き続きご利用いただけます</li>
                            <li>利用可能期間中に再契約しても、追加のお支払いは発生しません</li>
                            <li>翌月以降は自動的にご利用いただけなくなります</li>
                          </>
                        );
                      })()}
                    </ul>
                  </div>

                  {actionError && (
                    <div style={{ marginBottom: 'var(--hub-space-md)', color: 'var(--hub-danger)', fontSize: '0.875rem' }}>
                      {actionError}
                    </div>
                  )}

                  <div style={{ display: 'flex', gap: 'var(--hub-space-md)' }}>
                    <button className="hub-btn hub-btn-secondary" style={{ flex: 1 }} onClick={handleCloseCancelModal}>
                      キャンセル
                    </button>
                    <button
                      className="hub-btn"
                      style={{ 
                        flex: 1,
                        background: 'var(--hub-danger)',
                        color: 'white',
                        border: 'none'
                      }}
                      onClick={handleProceedToReasonForm}
                      disabled={!canManageLicenses}
                    >
                      解約する
                    </button>
                  </div>
                </>
              )}

              {/* ===== STEP 2: 解約理由ヒアリングフォーム ===== */}
              {cancelModalStep === 2 && (
                <>
                  <div style={{ 
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '12px'
                  }}>
                    <h2 style={{ 
                      fontFamily: 'var(--hub-font-display)',
                      fontSize: '1.125rem',
                      fontWeight: 700,
                      margin: 0,
                      color: 'var(--hub-text-primary)'
                    }}>
                      解約理由をお聞かせください
                    </h2>
                    <button onClick={handleCloseCancelModal} className="hub-btn hub-btn-ghost" style={{ padding: '8px' }}>
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ width: 20, height: 20 }}>
                        <line x1="18" y1="6" x2="6" y2="18"/>
                        <line x1="6" y1="6" x2="18" y2="18"/>
                      </svg>
                    </button>
                  </div>

                  {/* 解約対象モジュール情報（小さめ） */}
                  <div style={{ 
                    padding: '8px 12px',
                    background: 'var(--hub-bg-secondary)',
                    borderRadius: 'var(--hub-radius-sm)',
                    marginBottom: '12px',
                    border: '1px solid rgba(239, 68, 68, 0.2)'
                  }}>
                    <span style={{ fontWeight: 600, fontSize: '0.875rem' }}>{cancelTargetProduct.name}</span>
                    <span style={{ color: 'var(--hub-text-secondary)', fontSize: '0.8125rem', marginLeft: '8px' }}>
                      を解約
                    </span>
                  </div>

                  {/* サービス向上の説明 */}
                  <p style={{ 
                    fontSize: '0.8125rem', 
                    color: 'var(--hub-text-secondary)', 
                    lineHeight: 1.5,
                    margin: '0 0 12px 0'
                  }}>
                    よりよいサービスの提供のため、解約理由をお聞かせください。<br />
                    いただいたご意見は今後のサービス改善に活用させていただきます。
                  </p>

                  {/* 解約理由選択（複数選択可） */}
                  <div style={{ marginBottom: 'var(--hub-space-md)' }}>
                    <div style={{ 
                      fontSize: '0.875rem', 
                      fontWeight: 600, 
                      marginBottom: '6px',
                      color: 'var(--hub-text-primary)'
                    }}>
                      解約理由（複数選択可）
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                      {CANCEL_REASONS.map((reason) => (
                        <label
                          key={reason.id}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            padding: '6px 12px',
                            background: cancelReasons.includes(reason.id) 
                              ? 'rgba(59, 130, 246, 0.1)' 
                              : 'var(--hub-bg-secondary)',
                            borderRadius: 'var(--hub-radius-sm)',
                            cursor: 'pointer',
                            transition: 'all 0.15s ease',
                            border: cancelReasons.includes(reason.id)
                              ? '1px solid rgba(59, 130, 246, 0.3)'
                              : '1px solid transparent'
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={cancelReasons.includes(reason.id)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setCancelReasons([...cancelReasons, reason.id]);
                              } else {
                                setCancelReasons(cancelReasons.filter(r => r !== reason.id));
                              }
                            }}
                            style={{ 
                              width: 16, 
                              height: 16, 
                              accentColor: '#3b82f6',
                              cursor: 'pointer'
                            }}
                          />
                          <span style={{ fontSize: '0.875rem', color: 'var(--hub-text-primary)' }}>
                            {reason.label}
                          </span>
                        </label>
                      ))}
                    </div>
                  </div>

                  {/* 自由入力欄 */}
                  <div style={{ marginBottom: 'var(--hub-space-md)' }}>
                    <div style={{ 
                      fontSize: '0.875rem', 
                      fontWeight: 600, 
                      marginBottom: '6px',
                      color: 'var(--hub-text-primary)'
                    }}>
                      その他ご意見・ご要望（任意）
                    </div>
                    <textarea
                      value={cancelFreeComment}
                      onChange={(e) => setCancelFreeComment(e.target.value)}
                      placeholder="ご自由にご記入ください..."
                      style={{
                        width: '100%',
                        minHeight: '70px',
                        padding: '10px 12px',
                        borderRadius: 'var(--hub-radius-md)',
                        border: '1px solid var(--hub-border)',
                        fontSize: '0.875rem',
                        resize: 'vertical',
                        fontFamily: 'inherit',
                        background: 'var(--hub-bg-primary)',
                        color: 'var(--hub-text-primary)'
                      }}
                    />
                  </div>

                  {actionError && (
                    <div style={{ marginBottom: '12px', color: 'var(--hub-danger)', fontSize: '0.875rem' }}>
                      {actionError}
                    </div>
                  )}

                  <div style={{ display: 'flex', gap: '12px', marginTop: '4px' }}>
                    <button className="hub-btn hub-btn-secondary" style={{ flex: 1 }} onClick={handleCloseCancelModal}>
                      キャンセル
                    </button>
                    <button
                      className="hub-btn"
                      style={{ 
                        flex: 1,
                        background: 'var(--hub-danger)',
                        color: 'white',
                        border: 'none'
                      }}
                      onClick={handleConfirmCancel}
                      disabled={!canManageLicenses || submitting}
                    >
                      {submitting ? <LoadingSpinner size="small" /> : '解約を実行'}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default LicensesPage;

